import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth } from "../firebase/config";
import { getUserData, logoutUser } from "../services/authService";
import { canCreateDiagnostic, getUserDiagnostics } from "../services/diagnosticService";

function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [diagnostics, setDiagnostics] = useState([]);
  const [diagnosticLimit, setDiagnosticLimit] = useState(null);

  // ✅ useCallback pour éviter le warning
  const loadData = useCallback(async () => {
    setLoading(true);
    const userResult = await getUserData(user.uid, { forceRefresh: true });
    if (userResult.success) setUserData(userResult.data);

    const diagResult = await getUserDiagnostics(user.uid);
    if (diagResult.success) {
      const sorted = diagResult.diagnostics.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });
      setDiagnostics(sorted);
    }

    const limitResult = await canCreateDiagnostic(user.uid);
    setDiagnosticLimit(limitResult);

    setLoading(false);
  }, [user.uid]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    loadData();
  }, [user, navigate, loadData]);

  const handleLogout = async () => {
    await logoutUser();
    navigate("/login");
  };

  const getSubscriptionBadge = (type) => {
    const badges = {
      free: { text: t("free"), color: "bg-gray-500" },
      silver: { text: t("silver"), color: "bg-gray-400" },
      gold: { text: t("gold"), color: "bg-yellow-500" },
    };
    const badge = badges[type] || badges.free;
    return (
      <span className={`${badge.color} text-white px-3 py-1 rounded-full text-sm font-semibold`}>
        {badge.text}
      </span>
    );
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">{t("loading")}</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">{t("dashboard")}</h1>

          {/* Langues */}
          <div className="flex items-center gap-2 mr-4">
            {["fr", "en"].map((lang) => (
              <button
                key={lang}
                onClick={() => i18n.changeLanguage(lang)}
                className={`px-2 py-1 rounded border ${
                  i18n.language === lang ? "bg-gray-300" : ""
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

          <button
            onClick={() => navigate('/faq')}
            className="text-gray-600 hover:text-gray-900 font-semibold"
          >
            ❓ FAQ
          </button>

          {/* Actions */}
          <div className="flex gap-3">
            {userData?.role === "admin" && (
              <button
                onClick={() => navigate("/admin")}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg"
              >
                🔐 {t("admin")}
              </button>
            )}
            <button
              onClick={handleLogout}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg"
            >
              {t("logout")}
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Carte utilisateur */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {t("hello")} {user?.email} 👋
              </h2>
              <p className="text-gray-600">{t("currentSubscription")}</p>
            </div>
            {getSubscriptionBadge(userData?.subscriptionType)}
          </div>

          {/* Compteur diagnostics */}
          <div className="mt-4 bg-gray-50 rounded-lg p-4 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">{t("diagnosticsThisMonth")}</p>
              <p className="text-2xl font-bold text-gray-800">
                {userData?.diagnosticsThisMonth || 0} / {userData?.diagnosticsLimit || 1}
              </p>
            </div>
            {diagnosticLimit?.canCreate ? (
              <span className="text-green-600 font-semibold">
                {diagnosticLimit.remaining} {t("remaining")}
              </span>
            ) : (
              <div className="text-right">
                <span className="text-red-600 font-semibold block mb-1">{t("limitReached")}</span>
                <button
                  onClick={() => navigate("/pricing")}
                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold"
                >
                  {t("buyMore")}
                </button>
              </div>
            )}
          </div>

          {/* Bouton de rafraîchissement */}
          <div className="mt-4 text-center">
            <button
              onClick={loadData}
              className="text-sm text-gray-600 hover:text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-100 transition"
            >
              🔄 Actualiser mes données
            </button>
          </div>
        </div>

        {/* Actions rapides */}
        <div className="grid gap-6">
          <ActionCard
            emoji="🔍"
            title={t("newDiagnostic")}
            description={t("newDiagnosticDescription")}
            onClick={() => navigate("/new-diagnostic")}
            disabled={!diagnosticLimit?.canCreate}
          />
        </div>

        {/* Diagnostics récents */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t("recentDiagnostics")}</h2>
          {diagnostics.length === 0 ? (
            <p className="text-gray-600 text-center py-8">{t("noDiagnostics")}</p>
          ) : (
            <div className="space-y-3">
              {diagnostics.slice(0, 5).map((diag) => (
                <div
                  key={diag.id}
                  onClick={() => navigate(`/diagnostic-result/${diag.id}`)}
                  className="flex justify-between items-start p-4 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition"
                >
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {diag.projectName || t("untitled")}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {diag.createdAt?.toDate
                        ? new Date(diag.createdAt.toDate()).toLocaleDateString(i18n.language)
                        : new Date(diag.createdAt).toLocaleDateString(i18n.language)}
                    </p>
                  </div>
                  <button className="text-blue-600 hover:text-blue-700 font-semibold text-sm">
                    {t("view")} →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Component pour les actions rapides
function ActionCard({ emoji, title, description, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`bg-white p-6 rounded-lg shadow hover:shadow-lg transition text-left cursor-pointer ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      }`}
    >
      <div className="text-4xl mb-4">{emoji}</div>
      <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </button>
  );
}

export default Dashboard;
